// src/Asset.js
import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { AccessContext } from '../contexts/AccessProvider';
import { handleRetry } from '../../shared/utils/navigationUtils';
import { selfStartTask } from '../services/api/tasks';

import AssetCard from '../components/AssetCard';
import AssetDocumentsPanel from '../components/AssetDocumentsPanel';
import AssignedTaskCard from '../components/AssignedTaskCard';
import ErrorView from '../components/errors/ErrorView';
import LoadingView from '../../shared/components/LoadingView';
import TaskHistoryModal from '../components/modals/TaskHistoryModal';
import SelfStartWorkflowModal from '../components/modals/SelfStartWorkflowModal';
import { WorkflowTypeBadges } from '../../shared/components/WorkflowTypeBadge';

export default function Asset() {
  const { setTopBarProps } = useOutletContext();
  const { access_key } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { userProfile, markUserWorkflowAccess } = useContext(UserProfileContext);
  const { assetMap, loading, error, fetchAndValidateAccess, fetchAssetDetails } = useContext(AccessContext);
  const asset = assetMap[access_key]?.data;

  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showSelfStartModal, setShowSelfStartModal] = useState(false);
  const [isStartingTask, setIsStartingTask] = useState(false);
  /** Until fetchAndValidateAccess finishes for this URL key, avoid flashing Missing Asset (global loading skips new keys). */
  const [accessBootstrapDone, setAccessBootstrapDone] = useState(false);

  // Hide feedback (User Workflow) tasks from the maintenance log on the asset page;
  // they belong in the admin Feedback tab.
  const openTasks = useMemo(() => {
    const tasks = asset?.tasks ? Object.values(asset.tasks) : [];
    return tasks
      .filter((t) => !t.workflow?.user_workflow)
      .sort((a, b) => b.id - a.id);
  }, [asset]);
  const completedTasks = useMemo(
    () => (asset?.task_history || []).filter((t) => !t.workflow?.user_workflow),
    [asset]
  );
  const selfStartableWorkflows = asset?.self_startable_workflows || [];

  useEffect(() => {
    // Update the top bar specific for this page
    setTopBarProps({
      headerText: 'Asset Details',
    });
  }, [setTopBarProps]);


  useEffect(() => {
    if (!access_key) {
      setAccessBootstrapDone(true);
      return;
    }
    let cancelled = false;
    setAccessBootstrapDone(false);
    (async () => {
      try {
        await fetchAndValidateAccess(access_key);
      } finally {
        if (!cancelled) setAccessBootstrapDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access_key, fetchAndValidateAccess]);

  /**
   * Set by the Close Task button on its way here. Closing a task used to say
   * nothing at all: the work report went to the server and the screen just
   * changed, leaving the engineer to guess whether it had been received.
   */
  const [taskJustCompleted, setTaskJustCompleted] = useState(
    Boolean(location.state?.taskCompleted)
  );

  useEffect(() => {
    if (!location.state?.taskCompleted) return;
    // Drop the flag from the history entry so that a refresh, or coming back
    // here later, does not re-announce a task closed an hour ago.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const waitingForAsset = loading || !accessBootstrapDone;

  // Render error, loading, or "no data" states
  if (waitingForAsset) {
    return (
      <LoadingView
        title="Loading Your Asset"
        subtitle="Please wait while we fetch the latest information"
        extra="Connecting to server..."
        fullViewport
      />
    );
  }
  if (error) {
    return (
      <ErrorView
        title="Connection Lost"
        message="We couldn't load the latest data from the backend."
        onRetry={handleRetry}
        onGoBack={() => window.history.back()}
      />
    );
  }
  if (!asset) {
    return (
      <ErrorView
        title="Missing Asset"
        message={`These are not the droids you are looking for.`}
        onRetry={handleRetry}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  function handleStartTask(task) {
    navigate(`./task/${task.id}`, { relative: 'path' });
  }

  function handleWorkflowClick(workflow) {
    // User Workflows are anonymous — never gate them on profile completeness.
    // Maintenance workflows still require the visitor's field engineer profile.
    if (!workflow?.user_workflow && !userProfile?.field_engineer_id) {
      alert('Your profile is incomplete. Please log in again.');
      return;
    }
    setSelectedWorkflow(workflow);
    setShowSelfStartModal(true);
  }

  async function handleConfirmSelfStart() {
    if (!selectedWorkflow) return;

    // For user workflows we accept anonymous starts; for maintenance workflows
    // we still require a field engineer profile.
    if (!selectedWorkflow.user_workflow && !userProfile?.field_engineer_id) {
      return;
    }

    setIsStartingTask(true);
    try {
      const payload = { workflow_id: selectedWorkflow.id };
      if (userProfile?.field_engineer_id) {
        payload.field_engineer_id = userProfile.field_engineer_id;
      }

      const task = await selfStartTask(access_key, payload);

      // For a user workflow, suppress the profile modal in the downstream
      // workflow screens for the rest of this session.
      if (selectedWorkflow.user_workflow) {
        markUserWorkflowAccess(access_key);
      }

      // Refresh asset data immediately before navigating
      await fetchAssetDetails(access_key);

      // Close modal and navigate to the new task
      setShowSelfStartModal(false);
      setSelectedWorkflow(null);
      navigate(`./task/${task.id}`, { relative: 'path' });
    } catch (err) {
      console.error('Error starting task:', err);
      alert('Failed to start task. Please try again.');
    } finally {
      setIsStartingTask(false);
    }
  }

  function handleCancelSelfStart() {
    setShowSelfStartModal(false);
    setSelectedWorkflow(null);
  }

  /* eslint-disable jsx-a11y/no-access-key -- accessKey is domain auth token prop, not HTML accesskey */
  return (
    <>
      <div className="container mt-3">
        {taskJustCompleted && (
          <div
            className="alert alert-success alert-dismissible d-flex align-items-center gap-2"
            role="status"
          >
            <i className="bi bi-check-circle-fill flex-shrink-0" aria-hidden="true" />
            <span>Task completed. Your report has been submitted.</span>
            <button
              type="button"
              className="btn-close"
              aria-label="Dismiss"
              onClick={() => setTaskJustCompleted(false)}
            />
          </div>
        )}

        {/* Asset Info Card */}
        <AssetCard asset={asset} />

        {/* Render open tasks */}
        <h5>Open Tasks</h5>
        {openTasks.length > 0 ? (
          openTasks.map((task) => (
            <div key={task.id} className="mb-2">
              <AssignedTaskCard
                task={task}
                onStartTask={handleStartTask}
                accessKey={access_key}
              />
            </div>
          ))
        ) : (
          <p className="text-muted">No open tasks</p>
        )}

        {/* Task history link */}
        {completedTasks.length > 0 ? (
          <p>
            <button 
              type="button"
              className="btn btn-link w-100 text-muted p-0 border-0 text-start" 
              onClick={() => setShowTaskHistory(true)}
            >
              Task History ({completedTasks.length})
            </button>
          </p>
        ) : (
          <p className="text-muted">No task history</p>
        )}

        {/* Self-startable workflows section */}
        {selfStartableWorkflows.length > 0 && (
          <div className="mb-4">
            <h5>Start a New Task</h5>
            {selfStartableWorkflows.map((workflow) => (
              <div
                key={workflow.id}
                className="card mb-2 shadow-sm"
                style={{ cursor: 'pointer' }}
                onClick={() => handleWorkflowClick(workflow)}
              >
                <div className="card-body d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <span className="fw-semibold">{workflow.name}</span>
                    <WorkflowTypeBadges workflow={workflow} />
                  </span>
                  <i className="bi bi-chevron-right text-secondary"></i>
                </div>
              </div>
            ))}
          </div>
        )}

        {userProfile?.field_engineer_id && (
          <AssetDocumentsPanel
            accessKey={access_key}
            fieldEngineerId={userProfile.field_engineer_id}
          />
        )}
      </div>

      {/* Task History Modal */}
      {showTaskHistory && (
        <TaskHistoryModal
          tasks={completedTasks}
          onClose={() => setShowTaskHistory(false)}
        />
      )}

      {/* Self-Start Workflow Modal */}
      <SelfStartWorkflowModal
        show={showSelfStartModal}
        workflow={selectedWorkflow}
        onConfirm={handleConfirmSelfStart}
        onCancel={handleCancelSelfStart}
        isLoading={isStartingTask}
      />
    </>
  );
}
